import { Box, Button, Flex, Progress, SxStyleProp, Text } from 'theme-ui';
import { useDispatch, useSelector } from 'react-redux';
import { exportTemplate, getDownloadingState } from 'modules/downloading';
import { getScenes, getUsedPaidFeatures, uploadLogFile } from 'modules/scenes';
import { getUser, isSubscribed } from 'modules/user';
import { isUserSongUploading } from 'modules/userSongs';
import { getCurrentProjectName } from 'modules/projectFles';
import { SceneType, TempAudio, TempScenes } from 'types/Scene';
import { FC, memo, useCallback, useState } from 'react';
import { getPremiumData, showCheckoutModal } from 'modules/checkoutModal';
import { useTranslator } from '@picsart/rc/services/localization';
import { FilesAreUploadingModal } from './FilesAreUploadingModal';
import { ExportSettingsModal } from './ExportSettingsModal';
import { CONTENT_TEAM_MODE } from 'utils/env';
import Icon, { IconTypes } from 'shared/Icon';
import { XmlUploader } from './XmlUploader';
import { getExportProgress } from 'modules/template';
import ErrorLogger from '../ErrorLogger';
import { ProcessingTaskStatusStatusEnum } from 'api';

type HeaderProps = {
  downloadDisabled: boolean;
  goldHandler: () => void;
  isGoldButtonShown: boolean;
  exportTemplateHandler: () => void;
  isFilesUploading: boolean;
  exportProgress: number;
  uploadLogFileHandler: (tempScenes: TempScenes, tempAudio: TempAudio) => void;
  projectName: string;
  premiumFeatureUsed: boolean;
};

const exportBtnStyles: SxStyleProp = {
  ml: '8px',
  mr: '8px',
  px: '21px'
};

const exportGoldBtnStyles: SxStyleProp = {
  ...exportBtnStyles,
  backgroundImage: 'linear-gradient(164.31deg, #F3A53A 0%, #BF5794 52.31%, #A0258F 99.81%)',
  ':hover': {
    backgroundColor: 'black',
    backgroundImage: 'linear-gradient(164.31deg, #F3A53A5A 0%, #BF57945A 52.31%, #A0258F5A 99.81%)'
  }
};

const ErrorBoundary = ErrorLogger.getErrorBoundary();

const HeaderExportBase: FC<HeaderProps> = memo(
  ({
    downloadDisabled,
    isGoldButtonShown,
    exportTemplateHandler,
    isFilesUploading,
    exportProgress,
    uploadLogFileHandler,
    projectName,
    premiumFeatureUsed
  }) => {
    const [isOpenExportSetings, setOpenExportSettings] = useState(false);
    const [isOpenFilesUploading, setOpenFilesUploading] = useState(false);

    const translator = useTranslator();

    return (
      <div
        sx={{
          position: 'absolute',
          right: '0px',
          top: '0px',
          height: '62px',
          marginBottom: '4px',
          bg: 'background',
          borderRadius: '8px'
        }}
      >
        <Flex sx={{ alignItems: 'center', height: '100%' }}>
          <Flex>
            {CONTENT_TEAM_MODE && <XmlUploader uploadLogFileHandler={uploadLogFileHandler} />}
            {CONTENT_TEAM_MODE && (
              <Flex sx={{ width: '360px', mr: '10px', ml: '10px' }}>
                <Button
                  variant="secondary"
                  onClick={
                    isFilesUploading ? () => setOpenFilesUploading(true) : exportTemplateHandler
                  }
                  disabled={downloadDisabled}
                >
                  Export template
                </Button>
                {typeof exportProgress === 'number' && exportProgress !== 0 && (
                  <Box sx={{ flex: '1', pl: '20px', pt: '8px' }}>
                    <Text variant="body" sx={{ color: 'white', mb: '0' }}>
                      Exporting template...
                    </Text>
                    <Progress sx={{ mt: '0' }} value={exportProgress / 100} />
                  </Box>
                )}
              </Flex>
            )}
          </Flex>

          <Flex
            sx={{
              justifyContent: 'flex-end',
              flexGrow: 1
            }}
          >
            <Button
              variant="primary"
              sx={isGoldButtonShown && premiumFeatureUsed ? exportGoldBtnStyles : exportBtnStyles}
              onClick={() =>
                isFilesUploading ? setOpenFilesUploading(true) : setOpenExportSettings(true)}
              disabled={downloadDisabled}
              data-test="header_download-button"
            >
              <Icon
                name={isGoldButtonShown && premiumFeatureUsed ? 'gold-gen' : 'download-editor'}
                type={IconTypes.White}
                sx={{ mr: 2 }}
              />
              {translator('Export')}
            </Button>
          </Flex>
        </Flex>
        <ErrorBoundary>
          <FilesAreUploadingModal
            isOpen={isOpenFilesUploading}
            closeHandler={() => setOpenFilesUploading(false)}
          />
        </ErrorBoundary>
        <ExportSettingsModal
          isOpen={isOpenExportSetings}
          closeHandler={() => setOpenExportSettings(false)}
          name={projectName}
        />
      </div>
    );
  }
);

const HeaderExport = () => {
  const { status } = useSelector(getDownloadingState);
  const scenes = useSelector(getScenes);
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const userSongUploading = useSelector(isUserSongUploading);
  const isUserSubscribed = useSelector(isSubscribed);
  const isFilesUploading = userSongUploading || scenes.some(scene => scene.uploaded === false);
  const exportProgress = useSelector(getExportProgress);
  const premiumFeatureUsed = !!useSelector(getUsedPaidFeatures).length;
  const premiumItems = useSelector(getPremiumData);

  const projectName = useSelector(getCurrentProjectName);

  const downloadDisabled =
    status === ProcessingTaskStatusStatusEnum.Running ||
    status === ProcessingTaskStatusStatusEnum.Queued ||
    !scenes.some(item => item.type !== SceneType.EMPTY && item.src);

  const goldHandler = useCallback(() => dispatch(showCheckoutModal('gold_icon', premiumItems)), []);
  const exportTemplateHandler = useCallback(() => dispatch(exportTemplate()), []);
  const uploadLogFileHandler = useCallback(
    (tempScenes, audioName) => dispatch(uploadLogFile(tempScenes, audioName)),
    []
  );

  return (
    <HeaderExportBase
      goldHandler={goldHandler}
      downloadDisabled={downloadDisabled}
      isGoldButtonShown={user ? !isUserSubscribed : false}
      exportTemplateHandler={exportTemplateHandler}
      isFilesUploading={isFilesUploading}
      exportProgress={exportProgress}
      uploadLogFileHandler={uploadLogFileHandler}
      projectName={projectName}
      premiumFeatureUsed={premiumFeatureUsed}
    />
  );
};

export default HeaderExport;
